import { Stack, Avatar, Slider } from '@mui/material';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import Cropper from 'react-easy-crop';

import PersonIcon from 'components/v2/icons/PersonIcon';
import InputUploadZone from 'components/v2/input/InputUploadZone';
import Modal from 'components/v2/modal/Modal';
import { useLocalization } from 'localization';

import { BranchDetails } from 'api/branches/dto';
import { useBranchAvatar } from 'hooks/services/useBranchAvatar';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const STEP = 0.1;

type Props = {
  branchDetail?: BranchDetails;
};

const BranchAvatar = (props: Props) => {
  const { branchDetail } = props;
  const { t } = useLocalization();
  const { action, apiState, clientState } = useBranchAvatar();
  const { openUpload, rawImage, crop, zoom, wrapperRef, files } = clientState;
  const { isLoading } = apiState;
  const {
    setFiles,
    setCrop,
    setZoom,
    setImgSize,
    handleDrop,
    onCropComplete,
    handleConfirm,
    handleCancel,
    openModal,
    closeModal,
  } = action;

  return (
    <>
      <Stack
        ref={wrapperRef}
        justifyContent="center"
        alignItems="center"
        sx={{
          width: 128,
          height: 128,
          borderRadius: '50%',
          bgcolor: 'white',
          border: '2px solid white',
          position: 'relative',
          overflow: 'hidden',
          '&:hover .cameraOverlay, &:focus-within .cameraOverlay': { opacity: 1 },
        }}
      >
        {branchDetail?.profile?.avatarImageUrl ? (
          <Avatar
            src={branchDetail?.profile?.avatarImageUrl}
            alt="User avatar"
            sx={{ width: '100%', height: '100%' }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ width: '100%', height: '100%', borderRadius: '50%', bgcolor: 'grey.300', color: 'text.secondary' }}
          >
            <PersonIcon width="100%" height="90%" />
          </Stack>
        )}

        <Stack
          className="cameraOverlay"
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            transition: 'opacity 120ms linear',
            color: 'white',
          }}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            onClick={e => {
              e.stopPropagation();
              openModal();
            }}
            sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
          >
            <CameraAltRoundedIcon fontSize="large" />
          </Stack>
        </Stack>
      </Stack>

      <Modal
        modalTitle={rawImage ? t('accountSetting.cropImage') : t('accountSetting.uploadAvatar')}
        isOpen={openUpload}
        toggleModal={closeModal}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        disableConfirm={!rawImage}
        isLoading={isLoading}
      >
        <Stack sx={{ position: 'relative', height: 400 }} spacing={2}>
          {!rawImage && (
            <InputUploadZone
              text={t('modals.saveDocument.uploadInstructions')}
              hint="PNG/JPG"
              accept={['image/*']}
              multiple={false}
              onDrop={handleDrop}
              onChange={setFiles}
              files={files}
              setFiles={setFiles}
            />
          )}

          {rawImage && (
            <>
              <div style={{ position: 'relative', flex: 1 }}>
                <Cropper
                  image={rawImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={m => setImgSize({ width: m.naturalWidth, height: m.naturalHeight })}
                />
              </div>
              <Slider
                value={zoom}
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={STEP}
                onChange={(_, v) => setZoom(v as number)}
                size="small"
              />
            </>
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default BranchAvatar;
